import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Loader2, Copy, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { EmptyState, SectionHeader } from "@/components/PageHeader";
import BioRow from "@/components/bios/BioRow";
import BioEditDialog from "@/components/bios/BioEditDialog";
import BioDeadlineControl from "@/components/bios/BioDeadlineControl";
import BioNudgeBar from "@/components/bios/BioNudgeBar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const ALL = "all";

// Plain text for pasting into a program: "Name (Role)" line, bio, blank line. Members with a bio only.
const bioText = (items) => items.filter((m) => m.bio).map((m) => `${m.name}${m.role ? ` (${m.role})` : ""}\n${m.bio}`).join("\n\n");

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return; } catch { /* fall back below */ }
  const ta = document.createElement("textarea");
  ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
}

export default function CoachBios() {
  const { viewOnly } = useAuth();
  const [search] = useSearchParams();
  const [group, setGroup] = useState(search.get("subgroup") || ALL);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const subgroupId = group === ALL ? null : group;
  const params = subgroupId ? { subgroup_id: subgroupId } : {};

  const load = useCallback(() => {
    api.get("/bios/team", { params: group === ALL ? {} : { subgroup_id: group } }).then(({ data: d }) => setData(d))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); if (group !== ALL) setGroup(ALL); });
  }, [group]);
  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  const patch = (id, fields) => setData((d) => ({ ...d, items: d.items.map((m) => (m.member_id === id ? { ...m, ...fields } : m)) }));
  const missingIds = (data?.items || []).filter((m) => !m.bio).map((m) => m.member_id);
  const groupName = data?.subgroups.find((s) => s.id === group)?.name;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [openIds, setOpenIds] = useState(new Set());
  const toggleOpen = (id) => setOpenIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOpen = !!data?.items.length && data.items.every((m) => openIds.has(m.member_id));

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get("/bios/export", { params, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "bios.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Bios PDF downloaded");
    } catch { toast.error("Unable to export bios"); }
    finally { setBusy(null); }
  };

  const copyBios = async () => {
    const n = data.items.filter((m) => m.bio).length;
    if (!n) { toast.error("No bios to copy yet"); return; }
    try { await copyText(bioText(data.items)); toast.success(`Copied ${n} bio${n === 1 ? "" : "s"} — ready to paste`); }
    catch { toast.error("Couldn't copy to the clipboard"); }
  };

  return (
    <div>
      <SectionHeader subtitle="Team member bios for programs and playbills" testId="bios-section-header"
        actions={<>
          <Button variant="outline" className="rounded-full" disabled={!data} onClick={copyBios} data-testid="bios-copy-btn"><Copy className="mr-2 h-4 w-4" /> Copy bios</Button>
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={!!busy || !data} onClick={exportPdf} data-testid="bios-export-pdf">
            {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Export PDF
          </Button>
        </>} />

      <Card className="mb-5 flex flex-col gap-4 border-slate-200 p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between" data-testid="bios-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Subgroup</span>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-64" data-testid="bios-subgroup-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL} data-testid="bios-subgroup-all">All team members</SelectItem>
              {(data?.subgroups || []).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`bios-subgroup-${s.id}`}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {data && <BioDeadlineControl deadline={data.deadline} today={data.today} groupName={groupName} subgroupId={subgroupId} viewOnly={viewOnly} onChange={load} />}
        </div>
        {data && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-[180px]" data-testid="bios-progress">
              <p className="text-sm text-slate-600"><span className="font-bold tabular-nums text-slate-900">{data.completed}</span> of <span className="tabular-nums">{data.total}</span> bios complete</p>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${data.total ? Math.round((data.completed / data.total) * 100) : 0}%` }} />
              </div>
            </div>
          </div>
        )}
      </Card>

      {!data ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <EmptyState testId="bios-empty" title={groupName ? `No team members in ${groupName}` : "No team members yet"}
          description={groupName ? "Add members to this subgroup from Team Members → Groups." : "Approved team members will appear here."} />
      ) : (
        <>
          {!viewOnly && <BioNudgeBar missingIds={missingIds} selected={selected} setSelected={setSelected} groupName={groupName} subgroupId={subgroupId} onDone={load} />}
          <div className="mb-2 flex justify-end">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setOpenIds(allOpen ? new Set() : new Set(data.items.map((m) => m.member_id)))} data-testid="bios-toggle-all-btn">
              {allOpen ? <ChevronsDownUp className="mr-1.5 h-3.5 w-3.5" /> : <ChevronsUpDown className="mr-1.5 h-3.5 w-3.5" />} {allOpen ? "Close All" : "Open All"}
            </Button>
          </div>
          <div className="space-y-3" data-testid="bios-list">
            {data.items.map((m, i) => (
              <BioRow key={m.member_id} m={m} viewOnly={viewOnly} delay={Math.min(i, 10) * 30} onEdit={setEditing}
                selected={selected.has(m.member_id)} onSelect={toggle}
                open={openIds.has(m.member_id)} onToggle={toggleOpen}
                onReminded={(at) => patch(m.member_id, { reminded_at: at })} />
            ))}
          </div>
        </>
      )}

      <BioEditDialog member={editing} program={data?.program} subgroupId={subgroupId} groupName={groupName}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
